import { z } from 'zod'
import { recurringSeriesSchema } from './recurrence.ts'
import { slugifyTag } from './tags.ts'

/**
 * Group domain logic.
 *
 * The interesting part is `groupAccess`: one pure function that answers "what
 * may this actor do with this group?" It is the *same* function the API
 * enforces with and the clients hide buttons with, because a group leader who
 * can see a "Manage members" button on somebody else's group has already been
 * told something about that group.
 *
 * Everything it needs is passed in. It never reads a database and never sees a
 * `Grants` object — the caller reduces permissions to three booleans, which
 * keeps this package free of a dependency on @church/permissions.
 */

export const GROUP_VISIBILITIES = ['public', 'members', 'private'] as const
export type GroupVisibility = (typeof GROUP_VISIBILITIES)[number]

export const GROUP_ENROLLMENTS = ['open', 'request', 'invite'] as const
export type GroupEnrollment = (typeof GROUP_ENROLLMENTS)[number]

export const GROUP_MEMBER_ROLES = ['member', 'leader'] as const
export type GroupMemberRole = (typeof GROUP_MEMBER_ROLES)[number]

export const GROUP_MEMBER_STATUSES = ['requested', 'active', 'declined', 'archived'] as const
export type GroupMemberStatus = (typeof GROUP_MEMBER_STATUSES)[number]

/** How the actor stands in relation to *this* group. */
export type GroupRelationship = 'none' | 'requested' | 'member' | 'leader'

export interface GroupAccessInput {
  /** Holds `groups:admin` — owner, admin, campus admin. */
  isAdmin: boolean
  /** Holds `groups:write` — staff who run groups across the church. */
  isStaff: boolean
  /** Holds `groups:read` — any signed-in member of the organization. */
  canRead: boolean
  visibility: GroupVisibility
  enrollment: GroupEnrollment
  rosterVisibleToMembers: boolean
  relationship: GroupRelationship
  /** Archived groups are read-only history; nobody joins one. */
  archived?: boolean
  /** At or over capacity. */
  full?: boolean
}

export interface GroupAccess {
  /** The group exists, as far as this actor is concerned. */
  canView: boolean
  /** Who else is in it. Absence of this is the leader-scoping rule. */
  canViewRoster: boolean
  /** Name, description, schedule, location, capacity. */
  canEdit: boolean
  /** Approve requests, add/remove people, promote a leader. */
  canManageMembers: boolean
  canArchive: boolean
  canDelete: boolean
  canJoin: boolean
  canRequestToJoin: boolean
  canLeave: boolean
  /**
   * The study notes, the rota, the link everybody was sent.
   *
   * Deliberately looser than the roster: material a group hands round is for
   * the people in the group, whether or not that group shares its member list.
   * A private recovery group can keep its roster to its leaders and still give
   * its members the week's reading.
   */
  canViewResources: boolean
  /** Resources a group keeps for its leaders: the leader guide, the care notes. */
  canViewLeaderResources: boolean
  canManageResources: boolean
  /**
   * The online meeting link. Its members' (and staff's); someone browsing
   * sees it only for a group anyone may join on the spot, where it is no
   * more than the next step.
   */
  canViewMeetingLink: boolean
}

export function groupAccess(input: GroupAccessInput): GroupAccess {
  const { isAdmin, isStaff, relationship } = input
  const staffLevel = isAdmin || isStaff
  const leads = relationship === 'leader'
  const belongs = leads || relationship === 'member'
  const archived = input.archived ?? false

  // Visibility is about *finding* a group you are not in. Being in one always
  // beats it — a member of a private recovery group can see their own group.
  const canView =
    staffLevel ||
    belongs ||
    (input.canRead && input.visibility === 'public') ||
    relationship === 'requested'

  // The acceptance criterion in one expression: leadership grants the roster of
  // the group you lead, and nothing about any other.
  const canViewRoster =
    staffLevel || leads || (relationship === 'member' && input.rosterVisibleToMembers)

  const joinable = canView && !belongs && !archived && !input.full
  return {
    canView,
    canViewRoster,
    canEdit: (staffLevel || leads) && !archived,
    canManageMembers: (staffLevel || leads) && !archived,
    canArchive: staffLevel,
    // Deleting a group destroys its attendance history, so it stays with the
    // people who can already see all of it.
    canDelete: isAdmin,
    canJoin: joinable && input.enrollment === 'open',
    canRequestToJoin: joinable && input.enrollment === 'request' && relationship !== 'requested',
    canLeave: belongs,
    canViewResources: staffLevel || belongs,
    canViewLeaderResources: staffLevel || leads,
    canManageResources: (staffLevel || leads) && !archived,
    canViewMeetingLink: staffLevel || belongs || (canView && input.enrollment === 'open'),
  }
}

/** A group's own privacy choices; null on each means "as its type says". */
export interface GroupSettingOverrides {
  visibility: GroupVisibility | null
  enrollment: GroupEnrollment | null
  rosterVisibleToMembers: boolean | null
}

export interface GroupSettings {
  visibility: GroupVisibility
  enrollment: GroupEnrollment
  rosterVisibleToMembers: boolean
}

/**
 * What actually applies to a group: its own setting where it has one, its
 * type's otherwise. Every visibility and enrolment decision reads this, never
 * the type alone — a group listed against its type's wishes is exactly the
 * leak this exists to prevent.
 */
export function effectiveGroupSettings(
  type: GroupSettings,
  group: GroupSettingOverrides,
): GroupSettings {
  return {
    visibility: group.visibility ?? type.visibility,
    enrollment: group.enrollment ?? type.enrollment,
    rosterVisibleToMembers: group.rosterVisibleToMembers ?? type.rosterVisibleToMembers,
  }
}

// ---- tags ------------------------------------------------------------------

export interface GroupTagRef {
  id: string
  tagGroupId: string
}

export interface GroupTagGroupRef {
  id: string
  multiple: boolean
}

/**
 * The directory's tag filter as SQL wants it: one list per facet. A group
 * matches when it has *any* tag of each list (Tuesday or Thursday) and *every*
 * list (…and Young adults). Ids that are not known tags are dropped, so a
 * stale link filters by what still exists rather than by nothing.
 */
export function tagFilterByFacet(selected: readonly string[], tags: readonly GroupTagRef[]) {
  const byId = new Map(tags.map((tag) => [tag.id, tag]))
  const facets = new Map<string, string[]>()
  for (const id of new Set(selected)) {
    const tag = byId.get(id)
    if (!tag) continue
    facets.set(tag.tagGroupId, [...(facets.get(tag.tagGroupId) ?? []), id])
  }
  return [...facets.values()]
}

/**
 * Checks the tags a group is given. Every id must be a known tag, and a facet
 * that allows one choice (Life stage) gets at most one — a group cannot be
 * both "Young adults" and "Retired". Returns the problem in words, or null.
 */
export function checkGroupTags(
  tagIds: readonly string[],
  tags: readonly GroupTagRef[],
  tagGroups: readonly GroupTagGroupRef[],
  names: { tagGroup: (id: string) => string } = { tagGroup: () => 'That category' },
): string | null {
  const byId = new Map(tags.map((tag) => [tag.id, tag]))
  const multiple = new Map(tagGroups.map((group) => [group.id, group.multiple]))
  const perFacet = new Map<string, number>()
  for (const id of new Set(tagIds)) {
    const tag = byId.get(id)
    if (!tag) return 'One of those tags does not exist.'
    perFacet.set(tag.tagGroupId, (perFacet.get(tag.tagGroupId) ?? 0) + 1)
  }
  for (const [facet, count] of perFacet) {
    if (count > 1 && multiple.get(facet) !== true) {
      return `${names.tagGroup(facet)} takes one tag per group.`
    }
  }
  return null
}

/**
 * Picking a tag for a group: on or off, and in a one-choice category picking
 * one puts down the other — the way a radio button does. Returns the new ids.
 */
export function toggleGroupTag(
  current: readonly string[],
  tag: GroupTagRef,
  tags: readonly GroupTagRef[],
  tagGroups: readonly GroupTagGroupRef[],
): string[] {
  if (current.includes(tag.id)) return current.filter((id) => id !== tag.id)
  const multiple = tagGroups.find((group) => group.id === tag.tagGroupId)?.multiple ?? false
  if (multiple) return [...current, tag.id]
  const sameFacet = new Set(tags.filter((t) => t.tagGroupId === tag.tagGroupId).map((t) => t.id))
  return [...current.filter((id) => !sameFacet.has(id)), tag.id]
}

/**
 * How someone stood in a group at a meeting, for the attendance record:
 * a guest, or anyone not on the active roster, came as a visitor.
 */
export function attendanceRole(
  membership: { role: string; status: string } | undefined,
): 'leader' | 'member' | 'visitor' {
  if (!membership || membership.status !== 'active') return 'visitor'
  return membership.role === 'leader' ? 'leader' : 'member'
}

// ---- schemas ---------------------------------------------------------------

export const slugifyGroupType = slugifyTag

const typeName = z
  .string()
  .trim()
  .min(1, 'Give the group type a name')
  .max(60)
  .refine((v) => slugifyGroupType(v) !== '', 'Use at least one letter or number')

const description = z
  .string()
  .trim()
  .max(2000)
  .nullish()
  .transform((v) => (v === '' || v === undefined ? null : v))

export const createGroupTypeSchema = z.object({
  name: typeName,
  description,
  visibility: z.enum(GROUP_VISIBILITIES).default('public'),
  enrollment: z.enum(GROUP_ENROLLMENTS).default('request'),
  rosterVisibleToMembers: z.boolean().default(true),
})
export type CreateGroupTypeInput = z.infer<typeof createGroupTypeSchema>
export type CreateGroupTypeFormInput = z.input<typeof createGroupTypeSchema>

export const updateGroupTypeSchema = createGroupTypeSchema.partial()
export type UpdateGroupTypeInput = z.infer<typeof updateGroupTypeSchema>

export const createGroupSchema = z.object({
  groupTypeId: z.uuid('Choose a group type'),
  campusId: z.uuid().nullish(),
  name: z.string().trim().min(1, 'Give the group a name').max(120),
  description,
  location: z
    .string()
    .trim()
    .max(200)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  // 0 would be a group nobody can join; null is "no limit", which is the
  // honest way to say it.
  capacity: z.number().int().min(1).max(10_000).nullish(),
  schedule: recurringSeriesSchema.nullish(),
  contactEmail: z
    .email('That does not look like an email address')
    .max(254)
    .nullish()
    .or(z.literal('').transform(() => null)),
  virtualUrl: z
    .url('That does not look like a link')
    .max(2000)
    .refine((value) => /^https?:\/\//i.test(value), 'Links must start with http:// or https://')
    .nullish()
    .or(z.literal('').transform(() => null)),
})
export type CreateGroupInput = z.infer<typeof createGroupSchema>
export type CreateGroupFormInput = z.input<typeof createGroupSchema>

export const updateGroupSchema = createGroupSchema.partial()
export type UpdateGroupInput = z.infer<typeof updateGroupSchema>

/** A group's own privacy (staff only); null puts a setting back to its type's. */
export const groupSettingsSchema = z.object({
  visibility: z.enum(GROUP_VISIBILITIES).nullable().optional(),
  enrollment: z.enum(GROUP_ENROLLMENTS).nullable().optional(),
  rosterVisibleToMembers: z.boolean().nullable().optional(),
})
export type GroupSettingsInput = z.infer<typeof groupSettingsSchema>

const tagName = z
  .string()
  .trim()
  .min(1, 'Give it a name')
  .max(60)
  .refine((v) => slugifyTag(v) !== '', 'Use at least one letter or number')

export const createGroupTagGroupSchema = z.object({
  name: tagName,
  multiple: z.boolean().default(false),
  shownInDirectory: z.boolean().default(true),
})
export type CreateGroupTagGroupInput = z.infer<typeof createGroupTagGroupSchema>

export const updateGroupTagGroupSchema = z.object({
  name: tagName.optional(),
  multiple: z.boolean().optional(),
  shownInDirectory: z.boolean().optional(),
  position: z.number().int().min(0).max(1000).optional(),
})
export type UpdateGroupTagGroupInput = z.infer<typeof updateGroupTagGroupSchema>

export const groupTagSchema = z.object({
  name: tagName,
  position: z.number().int().min(0).max(1000).optional(),
})
export type GroupTagInput = z.infer<typeof groupTagSchema>

export const setGroupTagsSchema = z.object({
  tagIds: z.array(z.uuid()).max(50),
})

export const GROUP_RESOURCE_KINDS = ['link', 'file'] as const
export type GroupResourceKind = (typeof GROUP_RESOURCE_KINDS)[number]

/** `members` — the whole group; `leaders` — its leaders and staff only. */
export const GROUP_RESOURCE_VISIBILITIES = ['members', 'leaders'] as const
export type GroupResourceVisibility = (typeof GROUP_RESOURCE_VISIBILITIES)[number]

export const ATTENDANCE_ROLES = ['member', 'leader', 'visitor'] as const
export type AttendanceRole = (typeof ATTENDANCE_ROLES)[number]

/**
 * A link the group shares. `http(s)` only: a `javascript:` or `data:` URL in a
 * list of things members are invited to click is a hazard, not a resource.
 */
const shareableUrl = z
  .url('That does not look like a link')
  .max(2000)
  .refine((value) => /^https?:\/\//i.test(value), 'Links must start with http:// or https://')

export const createLinkResourceSchema = z.object({
  title: z.string().trim().min(1, 'Give it a name').max(120),
  description: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  url: shareableUrl,
  visibility: z.enum(GROUP_RESOURCE_VISIBILITIES).default('members'),
})
export type CreateLinkResourceInput = z.infer<typeof createLinkResourceSchema>
export type CreateLinkResourceFormInput = z.input<typeof createLinkResourceSchema>

export const createFileResourceSchema = z.object({
  title: z.string().trim().min(1, 'Give it a name').max(120),
  description: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
  /** The storage key handed out by the upload-url endpoint. */
  key: z.string().min(1).max(500),
  fileName: z.string().trim().min(1).max(200),
  visibility: z.enum(GROUP_RESOURCE_VISIBILITIES).default('members'),
})
export type CreateFileResourceInput = z.infer<typeof createFileResourceSchema>

export const joinRequestSchema = z.object({
  note: z
    .string()
    .trim()
    .max(500)
    .nullish()
    .transform((v) => (v === '' || v === undefined ? null : v)),
})
export type JoinRequestInput = z.infer<typeof joinRequestSchema>
